import { Link } from "react-router-dom";
import { Waves, Wind, Moon } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const FLOWER =
  "/media/photo-fleur.jpg";

const PILLARS = [
  {
    icon: Waves, title: "Lâcher-prise",
    text: "Porté par l'eau, le corps abandonne le contrôle : les tensions du dos, de la nuque et du mental se dissolvent naturellement, sans effort.",
  },
  {
    icon: Wind, title: "Respiration",
    text: "Le souffle devient le chef d'orchestre du soin. Il rythme les mouvements et guide le receveur vers un état de conscience profond.",
  },
  {
    icon: Moon, title: "Silence intérieur",
    text: "Au-delà du soin, le Janzu est une méditation en mouvement — un refuge intérieur de paix et de silence, pour qui le reçoit comme pour qui le donne.",
  },
];

export default function Philosophie() {
  const { tr } = useLang();
  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="philosophie-page">
      <div className="mx-auto max-w-7xl px-5 pt-14 md:px-10">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>{tr("La philosophie")}</SectionLabel>
              <blockquote className="mt-7 font-serif text-3xl md:text-4xl leading-snug text-foam">
                « Thérapie aquatique par excellence, le Janzu permet de relâcher et libérer
                les tensions du <em className="italic text-aqua">corps</em>, du{" "}
                <em className="italic text-aqua">cœur</em> et de l'
                <em className="italic text-aqua">esprit</em>. »
              </blockquote>
              <p className="mt-6 text-sm leading-relaxed text-foam/60">
                {tr("Développé à partir des techniques de Juan Villatoro Garza dans les années 1980, le Janzu se pratique dans une eau chaude où le corps n'a plus qu'à se confier. Un soin qui se transmet — et qui se reçoit d'abord.")}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="img-clip-b mt-10 overflow-hidden h-44">
                <img src={FLOWER} alt="Fleur d'hibiscus offerte dans les mains au bord de l'eau" loading="lazy" className="h-full w-full object-cover [object-position:center_38%]" />
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/formations"
                data-testid="philosophie-cta"
                className="mt-10 inline-block rounded-full border border-aqua/40 px-7 py-3.5 text-sm font-semibold text-aqua transition-all duration-300 hover:bg-aqua hover:text-abyss"
              >
                {tr("Découvrir les formations")}
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 * i} className="border-b border-aqua/12 first:border-t">
                <div className="flex gap-6 py-9 group">
                  <span className="mt-1 grid h-12 w-12 shrink-0 place-items-center rounded-full border border-aqua/30 text-aqua transition-all duration-500 group-hover:bg-aqua/10 group-hover:shadow-[0_0_24px_rgba(56,189,248,0.3)]">
                    <p.icon size={20} />
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-foam">{tr(p.title)}</h3>
                    <p className="mt-2 max-w-lg text-sm leading-relaxed text-foam/60">{tr(p.text)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
